export interface CarouselSlide<T> {
  data: T;
  realIndex: number;
}

export interface CarouselBreakpoint {
  minWidth: number;
  items: number;
}

/**
 * Carrusel infinito basado en clones de borde (sin librería externa).
 * Solo soporta navegación por flechas (sin drag), como el owl-carousel-o que reemplaza.
 */
export class InfiniteCarousel<T> {

  itemsPerView = 1;
  currentIndex = 0;
  extended: CarouselSlide<T>[] = [];
  transitionEnabled = true;

  private items: T[] = [];
  private animating = false;
  private resetTimeout: any;
  // Antes 700ms con "ease" (bastante parejo). Con el autoplay por defecto (3s quieto, después
  // desliza) esa velocidad relativa lo hacía sentir como un salto brusco seguido de una pausa
  // larga, más que un movimiento continuo. Un poco más de duración + una curva ease-in-out más
  // marcada (arranca y termina suave, acelera en el medio) lo hace sentir más "transicional".
  // transitionMs vive acá (no repetido como string en el template) para que el timeout que
  // deshace el loop infinito (ver `step()`) nunca se desincronice de lo que la transición CSS
  // real tarda — si algún día cambia, cambia en un solo lugar.
  readonly transitionMs = 900;
  readonly transitionEasing = 'cubic-bezier(0.65, 0, 0.35, 1)';

  constructor(
    private readonly mobileBreakpoint = 400,
    private readonly mobileItemsPerView = 1,
    private readonly desktopItemsPerView = 2,
    // Para reemplazar carruseles owl con más de dos tramos responsive: lista de
    // {minWidth, items} tipo "responsive" de owl, en vez del par mobile/desktop de arriba.
    private readonly breakpoints?: CarouselBreakpoint[]
  ) { }

  setItems(items: T[]): void {
    this.items = items || [];
    this.rebuild();
  }

  setViewportWidth(width: number): void {
    const next = this.breakpoints
      ? this.resolveItemsFromBreakpoints(width)
      : (width < this.mobileBreakpoint ? this.mobileItemsPerView : this.desktopItemsPerView);
    if (next !== this.itemsPerView) {
      this.itemsPerView = next;
      this.rebuild();
    }
  }

  get trackWidthPercent(): number {
    return this.itemsPerView > 0 && this.extended.length > 0
      ? (this.extended.length / this.itemsPerView) * 100
      : 100;
  }

  get slideWidthPercent(): number {
    return this.extended.length > 0 ? 100 / this.extended.length : 100;
  }

  get translatePercent(): number {
    return this.extended.length > 0 ? -(this.currentIndex * (100 / this.extended.length)) : 0;
  }

  /** CSS `transition` completo para el track — el template solo lo asigna, no arma el string
   *  ni decide duración/curva, para no terminar con esos valores duplicados en dos lugares. */
  get transitionStyle(): string {
    return this.transitionEnabled ? `transform ${this.transitionMs}ms ${this.transitionEasing}` : 'none';
  }

  next(): void {
    this.step(1);
  }

  prev(): void {
    this.step(-1);
  }

  private resolveItemsFromBreakpoints(width: number): number {
    const sorted = [...this.breakpoints].sort((a, b) => a.minWidth - b.minWidth);
    let items = sorted[0]?.items ?? 1;
    for (const breakpoint of sorted) {
      if (width >= breakpoint.minWidth) {
        items = breakpoint.items;
      }
    }
    return items;
  }

  private rebuild(): void {
    clearTimeout(this.resetTimeout);
    this.animating = false;
    this.transitionEnabled = false;

    const total = this.items.length;
    if (total === 0) {
      this.extended = [];
      this.currentIndex = 0;
      return;
    }

    const perView = Math.min(this.itemsPerView, total);
    const head = this.items.slice(-perView).map((data, i) => ({ data, realIndex: total - perView + i }));
    const body = this.items.map((data, realIndex) => ({ data, realIndex }));
    const tail = this.items.slice(0, perView).map((data, realIndex) => ({ data, realIndex }));

    this.extended = [...head, ...body, ...tail];
    this.currentIndex = perView;

    setTimeout(() => { this.transitionEnabled = true; }, 0);
  }

  private step(direction: 1 | -1): void {
    const total = this.items.length;
    if (total <= 1 || this.animating) {
      return;
    }

    this.animating = true;
    this.transitionEnabled = true;
    this.currentIndex += direction;

    clearTimeout(this.resetTimeout);
    this.resetTimeout = setTimeout(() => {
      const perView = Math.min(this.itemsPerView, total);
      if (this.currentIndex >= perView + total) {
        this.transitionEnabled = false;
        this.currentIndex -= total;
      } else if (this.currentIndex < perView) {
        this.transitionEnabled = false;
        this.currentIndex += total;
      }
      this.animating = false;
    }, this.transitionMs + 20);
  }
}
